import { useEffect, useState, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { TronSocketProvider } from "@/contexts/TronSocketContext";
import { WalletProvider } from "@/contexts/WalletContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { usePlatform } from "@/hooks/usePlatform";
import { supabase } from "@/integrations/supabase/client";
import WalletConnectModal from "@/components/WalletConnectModal";
import tronscopeLogo from "@/assets/tronscope-logo.png";
import Index from "./pages/Index";

// Lazy load non-critical pages for code splitting
const SearchPage = lazy(() => import("./pages/SearchPage"));
const WalletPage = lazy(() => import("./pages/WalletPage"));
const BlocksPage = lazy(() => import("./pages/BlocksPage"));
const TransactionsPage = lazy(() => import("./pages/TransactionsPage"));
const TokensPage = lazy(() => import("./pages/TokensPage"));
const ValidatorsPage = lazy(() => import("./pages/ValidatorsPage"));
const ResourcesPage = lazy(() => import("./pages/ResourcesPage"));
const StakingPage = lazy(() => import("./pages/StakingPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const ApiDocsPage = lazy(() => import("./pages/ApiDocsPage"));
const NodesPage = lazy(() => import("./pages/NodesPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const FaqPage = lazy(() => import("./pages/FaqPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const WalletSetupPage = lazy(() => import("./pages/WalletSetupPage"));
const RecoverPage = lazy(() => import("./pages/RecoverPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const SecurityPage = lazy(() => import("./pages/SecurityPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const IOSExplorerPage = lazy(() => import("./pages/IOSExplorerPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="flex items-center gap-2">
      <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
      <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
      <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
  </div>
);

// iOS native app redirect component
const IOSRedirect = ({ children }: { children: React.ReactNode }) => {
  const { isIOS, isNative } = usePlatform();
  const location = useLocation();
  const [iosAppMode, setIosAppMode] = useState<"wallet" | "explorer" | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch iOS app mode from database
  useEffect(() => {
    const fetchIosMode = async () => {
      try {
        const response = await supabase.functions.invoke("admin", {
          body: { action: "get_ios_app_mode" },
        });

        if (response.data?.success) {
          setIosAppMode(response.data.mode || "wallet");
        } else {
          setIosAppMode("wallet");
        }
      } catch (error) {
        console.error("Failed to fetch iOS app mode:", error);
        setIosAppMode("wallet");
      } finally {
        setIsLoading(false);
      }
    };

    if (isNative && isIOS) {
      fetchIosMode();
    } else {
      setIsLoading(false);
    }
  }, [isNative, isIOS]);

  const isIOSNative = isNative && isIOS;

  // Prevent flashing the web home (explorer) while deciding where to route iOS users.
  if (isIOSNative && location.pathname === "/") {
    if (isLoading || !iosAppMode) {
      return (
        <div
          className="min-h-screen bg-background flex flex-col items-center justify-center overflow-hidden"
          style={{
            paddingTop: "env(safe-area-inset-top)",
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
        >
          <div className="flex flex-col items-center justify-center animate-fade-in">
            {/* Animated loader - same as wallet loading */}
            <div className="relative mb-8">
              {/* Outer ring */}
              <div className="w-28 h-28 rounded-full border-4 border-primary/10 absolute inset-0 animate-pulse" />
              
              {/* Spinning gradient ring */}
              <div className="w-28 h-28 rounded-full border-4 border-transparent border-t-primary border-r-primary/50 animate-spin" style={{ animationDuration: '1.5s' }} />
              
              {/* Inner content - Sparkles icon like header */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 rounded-2xl bg-primary/15 border border-primary/20 flex items-center justify-center shadow-lg shadow-primary/20">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="32" 
                    height="32" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    className="text-primary animate-pulse drop-shadow-[0_0_8px_hsl(var(--primary)/0.6)]"
                  >
                    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                    <path d="M20 3v4" />
                    <path d="M22 5h-4" />
                    <path d="M4 17v2" />
                    <path d="M5 18H3" />
                  </svg>
                </div>
              </div>
              
              {/* Sparkle effects */}
              <div className="absolute -top-2 -right-2 w-3 h-3 rounded-full bg-primary/30 animate-ping" style={{ animationDuration: '2s' }} />
              <div className="absolute -bottom-1 -left-1 w-2 h-2 rounded-full bg-primary/40 animate-ping" style={{ animationDuration: '2.5s', animationDelay: '0.5s' }} />
            </div>
            
            {/* Loading text with animation */}
            <div className="text-center space-y-3">
              <h1 className="text-xl font-bold">
                Загрузка Tronscope
              </h1>
              <div className="flex items-center justify-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        </div>
      );
    }

    const to = iosAppMode === "explorer" ? "/ios-explorer" : "/wallet";
    return <Navigate to={to} replace />;
  }

  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <TronSocketProvider>
        <AuthProvider>
          <WalletProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <IOSRedirect>
                <WalletConnectModal />
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/search" element={<SearchPage />} />
                    <Route path="/wallet" element={<WalletPage />} />
                    <Route path="/blocks" element={<BlocksPage />} />
                    <Route path="/transactions" element={<TransactionsPage />} />
                    <Route path="/tokens" element={<TokensPage />} />
                    <Route path="/validators" element={<ValidatorsPage />} />
                    <Route path="/resources" element={<ResourcesPage />} />
                    <Route path="/staking" element={<StakingPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/api-docs" element={<ApiDocsPage />} />
                    <Route path="/nodes" element={<NodesPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/faq" element={<FaqPage />} />
                    <Route path="/auth" element={<AuthPage />} />
                    <Route path="/wallet-setup" element={<WalletSetupPage />} />
                    <Route path="/recover" element={<RecoverPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/security" element={<SecurityPage />} />
                    <Route path="/admin" element={<AdminPage />} />
                    <Route path="/ios-explorer" element={<IOSExplorerPage />} />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </IOSRedirect>
            </BrowserRouter>
          </WalletProvider>
        </AuthProvider>
      </TronSocketProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
